import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export function ImageUpload({ value, onChange, label = "Image", testId = "image-upload", aspect = "aspect-video", hint }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-cocoa">{label}</label>
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative ${aspect} w-full cursor-pointer overflow-hidden rounded-2xl border border-dashed border-cocoa/20 bg-cream transition-colors hover:border-terracotta/50`}
        data-testid={testId}
      >
        {value ? (
          <img src={fileUrl(value)} alt="upload preview" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-cocoa/40">
            {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus strokeWidth={1.5} className="h-7 w-7" />}
            <span className="text-sm">{uploading ? "Uploading…" : "Click to upload"}</span>
          </div>
        )}
        {value && !uploading && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-cocoa/70 text-warmwhite hover:bg-cocoa"
            data-testid={`${testId}-remove`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
      {hint && <p className="mt-1.5 text-xs text-cocoa/55" data-testid={`${testId}-hint`}>{hint}</p>}
    </div>
  );
}
